<template>
  <div>
    <TitleWithButton
      :title="pageTitle"
      :skeleton="isLoadingAsyncData"
      :button-text="Model.page_title.create"
      :creatable="creatable"
      class="mt-1 mb-4 font-weight-bold"
      style="font-size: 16px;"
      @create="onActionCreate"
    >
      <template v-if="list && list.length && deletable" #before-create>
        <v-skeleton-loader :loading="isLoadingAsyncData" type="button">
          <v-btn
            v-if="selected.length"
            dense
            depressed
            small
            class="ml-2"
            @click.stop="onDeleteSelected"
            v-text="'Удалить Выбранные'"
          />
          <v-btn
            dense
            depressed
            small
            class="ml-2"
            @click.stop="onDeleteAll"
            v-text="'Удалить все'"
          />
        </v-skeleton-loader>
      </template>
    </TitleWithButton>

    <BListFilter
      v-model="filters"
      label="Поиск файла по названию"
      :skeleton="isLoadingAsyncData"
      dense
      @change="onChangeFilter"
    />

    <BTableCommon
      v-model="selectedValue"
      :list="list"
      :actions="actions"
      :pagination="pagination"
      :ordering="filters.ordering"
      :loading="isLoading || isLoadingAsyncData"
      type="publication-storage-files"

      :has-users="defaultParams.hasUsers"

      @change:pagination="onChangePagination"
      @change:sort="onChangeSort"

      @action:edit="onActionEdit"
      @action:view="onActionView"
      @action:delete="onActionDelete"
    />
  </div>
</template>


<script>
import { StorageTypes } from '@/typings/api/Publication'

import { createModalRemove } from '@/helpers/createModalRemove'
import { lkPublicationStorageFilePermissions as Permissions } from '@/helpers/enums/permission-models'
import { createListMixin } from '@/helpers/mixins/createListMixin'
import { PublicationStorageFileModel as Model } from './Model'
import {
  publicationStorageFilesStoreModule as StoreModule,
  publicationStorageFilesStoreMapper as StoreMapper,
} from './store'
import { createPublicationStorageFilesModal } from './utils'

import BListFilter from '@/components/forms/BListFilter.vue'
import BTableCommon from '@/components/tables/BTableCommon.vue'
import TitleWithButton from '@/components/common/TitleWithButton.vue'
import { DELETE, EDIT, VIEW } from '@/helpers/enums/table-actions'


const mixinOptions = { Model, StoreModule }
const listMixin = createListMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createListMixin').ListMixin<any, StoreModule> } List */

export default {
  name: 'PublicationStorageFilesList',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BListFilter,
    BTableCommon,
    TitleWithButton,
  },
  mixins: [
    listMixin,
  ],
  props: {
    publication: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'id' in prop
      ),
    },
    storage: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'id' in prop
        && 'storage_type' in prop
        && Object.values(StorageTypes).includes(prop.storage_type)
      ),
    },
  },
  data() {
    const { storage } = this

    return {
      selected: [],
      disabledPaginationQuery: true,
      defaultParams: {
        publication_id: this.publication.id,
        storage_id: storage.id,
        hasUsers: storage.storage_type === StorageTypes.individual,
      },
    }
  },
  computed: {
    selectedValue: {
      get() {
        return this.deletable ? this.selected : void 0
      },
      set(val) {
        this.selected = val
      },
    },
    creatable() {
      return this.$access(Permissions.create)
    },
    deletable() {
      return this.$access(Permissions.delete)
    },
    editable() {
      return this.$access(Permissions.edit)
    },
    viewOnly() {
      return !this.$access(Permissions.edit) && !this.$access(Permissions.create) && !this.$access(Permissions.delete)
    },
    actions() {
      const actions = []
      if (this.editable) actions.push(EDIT)
      if (this.viewOnly) actions.push(VIEW)
      if (this.deletable) actions.push(DELETE)

      return actions
    },
  },
  /** @type { List } */
  methods: {
    ...StoreMapper.mapActions(['deleteAll']),

    onActionCreate() {
      const readonly = false
      this.onOpenModal(null, readonly)
    },
    onActionEdit(data) {
      const readonly = false
      this.onOpenModal(data, readonly)
    },
    onActionView(data) {
      const readonly = true
      this.onOpenModal(data, readonly)
    },
    async onActionDelete(data) {
      await this.onDeleteWithRefresh(data, this.defaultParams, false)
      this.$emit('update')
    },
    async onOpenModal(data, readonly) {
      const propsData = await this.getPropsData(data, readonly)
      const modal = await createPublicationStorageFilesModal(this, propsData)
      await modal.open()
      this.onGetData(this.filters)
      this.$emit('update')
    },
    async getPropsData(dataParams, readonly = false, callback) {
      let data = dataParams

      if (dataParams) {
        const options = {
          ...this.defaultParams,
          payload: { ...dataParams, ...this.defaultParams },
          id: dataParams.id,
        }

        const unwatch = callback?.()
        data = await this.getOne(options).catch(() => dataParams)
        unwatch?.()
      }


      const action = (payload) => {
        const options = {
          ...this.defaultParams,
          payload: { ...payload, ...this.defaultParams },
          id: payload.id,
        }

        const promise = payload.id
          ? this.update(options)
          : this.create(options)

        return promise
      }

      const propsData = {
        data,
        readonly,
        viewOnly: this.viewOnly,
        storage: this.storage,
        publication: this.publication,
        actionSave: action,
        actionDelete: this.onActionDelete,
        successText: data ? Model.response_success.update : Model.response_success.create,
        errorText: data ? Model.response_error.update : Model.response_error.create,
        ...this.defaultParams,
      }

      return propsData
    },
    async onDeleteSelected() {
      const result = await this.onDeleteMultipleWithRefresh(this.selected)
      if (result) this.$emit('update')
    },
    async onDeleteAll() {
      const result = await this.onDeleteAllWithRefresh()
      if (result) this.$emit('update')
    },
    async onDeleteAllWithRefresh() {
      const propsData = {
        titleText: 'Удаление всех файлов',
        action: () => this.deleteAll(this.defaultParams),
      }

      const modal = await createModalRemove(this, propsData)
      return modal.open()
    },
  },
}
</script>
